import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { TONE_CLASS } from 'shared/components/tone-banner'
import { Alert, AlertDescription, AlertTitle } from 'shared/components/ui/alert'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * The Point's closing above its remainders (ticket 45, decision 6): «Пункт временно
 * закрыт с <дата> — <комментарий>» when the admin closed it — the Point still works,
 * the hub only keeps its Parcels — or closed for good. An open Point shows nothing:
 * its week is the admin's business on «Локации», not this screen's.
 * The server's answer (`locations.PointState`); the operator reads their own Point.
 */
export type PointState = RouterOutputs['locations']['PointState']

export function PointStateBanner({ pointId }: { pointId?: string }) {
	const query = useQuery(trpc.locations.PointState.queryOptions(pointId ? { pointId } : undefined))
	if (!query.data) return null
	return <PointStateView state={query.data} />
}

/** A function of the server's answer: the closing in the attention tone, or nothing. */
export function PointStateView({ state }: { state: PointState }) {
	const { t } = useTranslate()
	if (state.closure !== 'temporarily' && state.closure !== 'for_good') return null
	const forGood = state.closure === 'for_good'
	const words = { date: formatDayTime(state.closedSince ?? undefined), comment: state.closureComment ?? '' }
	return (
		<Alert
			role='status'
			className={TONE_CLASS.attention}
			data-testid={forGood ? 'point-state-closed-for-good' : 'point-state-closed'}
		>
			<AlertTriangle />
			<AlertTitle>
				{t(forGood ? 'remainders.closedForGood' : 'remainders.closedTemporarily', words)}
			</AlertTitle>
			<AlertDescription className='text-current opacity-90'>
				{t(forGood ? 'remainders.closedForGoodNote' : 'remainders.closedTemporarilyNote')}
			</AlertDescription>
		</Alert>
	)
}
